import { ref, computed, type Ref } from 'vue'
import { sdk } from '@/graphql/client'

export interface ZoneSlot {
    id: number
    start: string | null
    end: string | null
}

export interface Zone {
    id: number
    name: string
    validSince: number | null
    validUntil: number | null
    slots: ZoneSlot[]
}

export interface ZoneFilters {
    search: string
}

function normalize(s: string): string {
    return s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
}

export function useZonesData() {
    const zones: Ref<Zone[]> = ref([])
    const loading = ref(false)
    const error = ref<string | null>(null)
    const filters: Ref<ZoneFilters> = ref({ search: '' })

    async function refetch() {
        loading.value = true
        error.value = null
        try {
            const r = await sdk.AdminZonesList()
            const raw = r.admin?.zones?.list ?? []
            zones.value = raw
                .filter((z): z is NonNullable<typeof z> => z !== null && z.id !== null)
                .map(z => ({
                    id: Number(z.id),
                    name: z.name ?? '',
                    validSince: z.validSince ?? null,
                    validUntil: z.validUntil ?? null,
                    slots: (z.slots ?? [])
                        .filter((s): s is NonNullable<typeof s> => s !== null && s.id !== null)
                        .map(s => ({
                            id: Number(s.id),
                            start: s.start ?? null,
                            end: s.end ?? null
                        }))
                }))
        } catch (e) {
            error.value = (e as Error).message
            zones.value = []
        } finally {
            loading.value = false
        }
    }

    const filteredZones = computed<Zone[]>(() => {
        const q = filters.value.search ? normalize(filters.value.search) : ''
        const filtered = zones.value.filter(z => !q || normalize(z.name).includes(q))
        return filtered.slice().sort((a, b) => a.name.localeCompare(b.name, 'cs'))
    })

    return { zones, filters, filteredZones, loading, error, refetch }
}
